/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery as useApolloQuery, useApolloClient } from '@apollo/client/react';
import { GET_ORGANIZATIONS } from '../../../lib/graphql/queries/organizations';
import {
  MappedOrganization,
  mapGQLOrganization,
  GQLEmailConfig,
} from './useOrganization';

// GQL response shape for the list
interface GQLOrganizationListItem {
  organizationId: string;
  orgName: string;
  orgPhone: string | null;
  active: boolean;
  completedSetup: boolean;
  acceptedTerms: boolean;
  activeSubscription: boolean | null;
  beta: boolean | null;
  email: string | null;
  notificationEmail: string | null;
  timezone: string | null;
  domain: string | null;
  assistantName: string | null;
  assistantNamePronunciation: string | null;
  clientAddress: string | null;
  primaryContact: string | null;
  subscription: string | null;
  callSettings: string | null;
  phoneNumbers: string | null;
  branding: string | null;
  logoUrl: string | null;
  combinedName: string | null;
  subdomain: string | null;
  calendlyUrl: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  twilioSecretRef: string | null;
  syncLink: string | null;
  stripeAccountId: string | null;
  stripeCustomerId: string | null;
  connectedCrm: string | null;
  isOnboardingComplete: boolean | null;
  activeClient: boolean | null;
  externalClient: boolean | null;
  emailCampaignsEnabled: boolean | null;
  dnsConfigured: boolean | null;
  smsAllOutcomes: boolean | null;
  orgLinks: string | null;
  fubSetup: string | null;
  hubspot: string | null;
  onboardingSetup: string | null;
  onboardingData: string | null;
  settings: string | null;
  sms: string | null;
  rolloutConfig: string | null;
  remiIntroName: string | null;
  cincSetup: string | null;
  oauthSetup: string | null;
  lastInitialSync: string | null;
  databaseUploads: string | null;
  integrationSetupRequests: string | null;
  databaseSize: number | null;
  brokerageName: string | null;
  clientOwner: string | null;
  users: never[];
  emailConfig: GQLEmailConfig | null;
}

interface GetOrganizationsData {
  organizations: {
    organizations: GQLOrganizationListItem[];
    total: number;
    hasMore: boolean;
  };
}

// Extended type matching the existing OrganizationWithTenant pattern
export interface OrganizationWithGQL extends MappedOrganization {
  hasTenant?: boolean;
}

const SORT_FIELD_MAP: Record<string, string> = {
  org_name: 'orgName',
  orgName: 'orgName',
  active: 'active',
  activeClient: 'active',
  lastInitialSync: 'lastInitialSync',
  brokerageName: 'brokerageName',
  databaseSize: 'databaseSize',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
};

export const useOrganizations = () => {
  const client = useApolloClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState('orgName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const backendSortBy = SORT_FIELD_MAP[sortField] || 'orgName';

  const { data, loading, error, refetch: apolloRefetch } = useApolloQuery<GetOrganizationsData>(
    GET_ORGANIZATIONS,
    {
      variables: {
        limit: pageSize,
        offset: currentPage * pageSize,
        search: debouncedSearch || undefined,
        activeOnly: false,
        sortBy: backendSortBy,
        sortOrder: sortDirection.toUpperCase(),
      },
      // cache-first: serves from cache after initial load — prevents in-flight
      // GQL requests from overwriting Apollo cache after a Remi API PUT.
      // Use refetch() explicitly (Refresh button) to get fresh data.
      fetchPolicy: 'cache-first',
      // notifyOnNetworkStatusChange off: prevents extra re-renders (and brief
      // data:undefined flashes) when the cache is written to via writeQuery.
    }
  );

  const rawOrgs = data?.organizations?.organizations ?? [];
  const totalCount = data?.organizations?.total ?? 0;

  const organizations = useMemo(
    () => rawOrgs.map((o) => mapGQLOrganization(o) as OrganizationWithGQL),
    [rawOrgs]
  );

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNext = currentPage < totalPages - 1;
  const hasPrev = currentPage > 0;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page on search change
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  const handleNextPage = useCallback(() => {
    if (hasNext) setCurrentPage((p) => p + 1);
  }, [hasNext]);

  const handlePrevPage = useCallback(() => {
    if (hasPrev) setCurrentPage((p) => p - 1);
  }, [hasPrev]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(0);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const refetch = useCallback(async () => {
    setCurrentPage(0);
    await apolloRefetch();
  }, [apolloRefetch]);

  /**
   * Write updated org fields directly into the Apollo cache without a network
   * request. Use this after a Remi API mutation to show saved values immediately —
   * the GQL backend returns null for some fields right after a PUT, so we own
   * the cache update ourselves instead of relying on refetch.
   */
  const updateOrgInCache = useCallback(
    (
      orgId: string,
      updates: {
        orgName?: string;
        orgPhone?: string;
        email?: string;
        syncLink?: string;
        clientOwner?: string | null;
        notificationEmail?: string;
        clientAddress?: string;
        timezone?: string;
      }
    ) => {
      const variables = {
        limit: pageSize,
        offset: currentPage * pageSize,
        search: debouncedSearch || undefined,
        activeOnly: false,
        sortBy: backendSortBy,
        sortOrder: sortDirection.toUpperCase(),
      };
      try {
        const current = client.readQuery<GetOrganizationsData>({
          query: GET_ORGANIZATIONS,
          variables,
        });
        if (!current) return;
        client.writeQuery({
          query: GET_ORGANIZATIONS,
          variables,
          data: {
            organizations: {
              ...current.organizations,
              organizations: current.organizations.organizations.map((org) =>
                org.organizationId === orgId ? { ...org, ...updates } : org
              ),
            },
          },
        });
      } catch (e) {
        console.error('[useOrganizations] updateOrgInCache failed:', e);
      }
    },
    [client, pageSize, currentPage, debouncedSearch, backendSortBy, sortDirection]
  );

  return {
    data: organizations,
    isLoading: loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    pageSize,
    hasNext,
    hasPrev,
    sortField,
    sortDirection,
    searchQuery,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handlePageSizeChange: setPageSize,
    handleSort,
    handleSearch,
    refetch,
    updateOrgInCache,
  };
};
